// src/App.js - Corrected version integrating with your existing structure
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { auth } from './firebase'; // Import auth from your firebase.js
import { onAuthStateChanged, signOut } from 'firebase/auth'; // Import Firebase Auth methods

// Import your existing components
import PropertyListings from './components/Public/PropertyListings';
import PropertyDetails from './components/Public/PropertyDetails';
import PropertyManager from './components/Admin/PropertyManager';
import LocationManager from './components/Admin/LocationManager';
import ProjectNameManager from './components/Admin/ProjectNameManager';
import AuthComponent from './components/Auth/Auth';
import AdminDashboard from './components/Admin/AdminDashboard';

// Import new SEO components (these should now work correctly)
import CategoryListings from './components/Public/CategoryListings';
import LocationListings from './components/Public/LocationListings';
import PriceRangeListings from './components/Public/PriceRangeListings';
import TagListings from './components/Public/TagListings';
import SearchResults from './components/Public/SearchResults';
import NotFound from './components/Public/NotFound';

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error logging out:", error);
      alert("Failed to log out. Please try again.");
    }
  };

  return (
    <>
      {/* Global SEO Meta Tags */}
      <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Kay Hooy Property" />
        <link rel="icon" href="/favicon.ico" />
        
        {/* Default meta tags */}
        <title>Property Listings Malaysia | Kay Hooy</title>
        <meta name="description" content="Find the best properties in Malaysia. Browse apartments, condos, and houses with detailed listings and competitive prices." />
        
        {/* Open Graph defaults */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Kay Hooy Property Listings" />
        
        {/* Schema.org markup for Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "RealEstateAgent",
            "name": "Kay Hooy Property",
            "url": typeof window !== 'undefined' ? window.location.origin : '',
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+60164444558",
              "contactType": "customer service"
            }
          })}
        </script>
      </Helmet>

      <Router>
        <nav style={navStyle}>
          <div style={leftNavGroupStyle}>
            <a href="https://kayhooy.com" target="_blank" rel="noopener noreferrer" style={navLinkStyle}>
              Home
            </a>
            <Link to="/" style={navLinkStyle}>
              Listings
            </Link>
          </div>

          <div style={rightNavGroupStyle}>
            {user ? (
              <>
                <Link to="/admin" style={adminLinkStyle}>
                  Admin
                </Link>
                <button onClick={handleLogout} style={logoutButtonStyle}>
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" style={adminLinkStyle}>
                Admin | Login
              </Link>
            )}
          </div>
        </nav>

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PropertyListings />} />
          <Route path="/listings" element={<PropertyListings />} />
          
          {/* Property details - supports both slug and ID */}
          <Route path="/listing/:id" element={<PropertyDetails />} />
          
          {/* NEW SEO Routes */}
          <Route path="/category/:category" element={<CategoryListings />} />
          <Route path="/location/:location" element={<LocationListings />} />
          <Route path="/price-range/:range" element={<PriceRangeListings />} />
          <Route path="/tag/:tag" element={<TagListings />} />
          <Route path="/search" element={<SearchResults />} />

          {/* Authentication Routes */}
          <Route path="/login" element={<AuthComponent />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/properties" element={<PropertyManager />} />
          <Route path="/admin/locations" element={<LocationManager />} />
          <Route path="/admin/project-names" element={<ProjectNameManager />} />
          
          {/* 404 Not Found - this should be the last route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </>
  );
}

// --- Inline Styles for App.js Navigation ---
const navStyle = {
  padding: '15px 25px',
  backgroundColor: '#34495e',
  borderBottom: '1px solid #2c3e50',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '25px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
};

const leftNavGroupStyle = {
  display: 'flex',
  gap: '30px',
};

const rightNavGroupStyle = {
  display: 'flex',
  gap: '15px',
  alignItems: 'center',
};

const navLinkStyle = {
  textDecoration: 'none',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '1.1em',
  padding: '8px 15px',
  borderRadius: '5px',
  transition: 'background-color 0.3s ease',
};

const adminLinkStyle = {
  ...navLinkStyle,
};

const logoutButtonStyle = {
  ...navLinkStyle,
  backgroundColor: '#dc3545',
  border: 'none',
  cursor: 'pointer',
};

export default App;